import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import BottomNav from "../components/BottomNav";
import Avatar from "../components/Avatar";
import { getGlobalLeaderboard } from "../lib/api";
import { Trophy, Crown, TrendingUp, Calendar, CalendarDays, Medal } from "lucide-react";

const TABS = [
  { v: "richest", l: "Richest", icon: Crown, color: "#FFD600" },
  { v: "growth", l: "Top Growth", icon: TrendingUp, color: "#00FF66" },
  { v: "daily", l: "Daily", icon: Calendar, color: "#00F0FF" },
  { v: "weekly", l: "Weekly", icon: CalendarDays, color: "#B026FF" },
  { v: "season", l: "Season", icon: Trophy, color: "#FF0055" },
  { v: "contests", l: "All Contests", icon: Medal, color: "#FFD600" },
];

const fmt = (n) => Number(n || 0).toLocaleString("en-IN");

export default function Leaderboard() {
  const [tab, setTab] = useState("richest");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getGlobalLeaderboard(tab).then(setRows).finally(() => setLoading(false));
  }, [tab]);

  const metricOf = (r) => {
    if (tab === "richest") return `${fmt(r.fan_credits)} credits`;
    if (tab === "growth") return `${(r.metric || 0) >= 0 ? "+" : ""}${fmt(r.metric)} growth`;
    if (tab === "daily" || tab === "weekly" || tab === "season") {
      const won = r.credits_won || 0;
      if (won > 0) return `+${fmt(won)} won · ${r.wins || 0} contest${(r.wins || 0) !== 1 ? "s" : ""}`;
      return `${fmt(r.points)} pts · ${r.wins || 0} lineup${(r.wins || 0) !== 1 ? "s" : ""}`;
    }
    if (tab === "contests") return `${fmt(r.points)} pts · ${r.lineups || 0} lineups`;
    return "";
  };

  return (
    <div className="min-h-screen pb-safe" data-testid="leaderboard-page">
      <Header title="Leaderboard" />
      <div className="max-w-xl mx-auto px-4 pt-4 space-y-3">
        <h1 className="font-display text-3xl font-bold">Leaderboard</h1>
        <p className="text-sm text-white/60 -mt-1">Track who's making the biggest moves on Fanpuff.</p>

        <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1">
          {TABS.map(t => (
            <button
              key={t.v}
              data-testid={`lb-tab-${t.v}`}
              onClick={() => setTab(t.v)}
              className="chip whitespace-nowrap px-3 py-1.5 text-xs inline-flex items-center gap-1"
              style={tab === t.v
                ? { background: `${t.color}22`, color: t.color, border: `1px solid ${t.color}66` }
                : { background: "rgba(255,255,255,0.05)", color: "#fff", border: "1px solid rgba(255,255,255,0.1)" }}
            >
              <t.icon className="w-3 h-3" /> {t.l}
            </button>
          ))}
        </div>

        {loading && <div className="text-white/50 text-sm">Loading…</div>}
        <div className="glass rounded-2xl divide-y divide-white/5">
          {rows.map(r => (
            <Link
              key={r.user_id}
              to={`/u/${r.user_id}`}
              className="flex items-center gap-3 p-3 hover:bg-white/5"
              data-testid={`lb-row-${r.rank}`}
            >
              <span className="w-7 text-center font-mono-brand text-sm text-white/60">#{r.rank}</span>
              <Avatar name={r.name} category="default" size={36} rounded={10} showSymbol={false} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{r.name}</p>
                {r.fan_credits != null && (
                  <p className="text-[10px] text-white/45 font-mono-brand">{fmt(r.fan_credits)} wallet</p>
                )}
              </div>
              <span className="text-xs font-mono-brand text-white/80 text-right">{metricOf(r)}</span>
            </Link>
          ))}
          {!loading && rows.length === 0 && <p className="p-4 text-sm text-white/50">No data yet — be the first to climb this board!</p>}
        </div>
        <div className="h-10" />
      </div>
      <BottomNav />
    </div>
  );
}
